import { Box, Typography, Tabs, Tab } from "@mui/material";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useUploadAssetContext } from "../../context/UploadAssetContext";
import DraggableImageItem from "./DraggableImageItem";

const ImageOrderList = () => {
  const {
    asset: editingAsset,
    handleAssetChange,
    setCurrentImageType,
    currentImageType,
    imageCategories, categoryKeys
  } = useUploadAssetContext();

  // --- Handlers remain in the parent component that manages the state ---

  const moveImage = (dragIndex: number, hoverIndex: number) => {
    if (!imageCategories[currentImageType]) return;
    
    const newImageList = [...imageCategories[currentImageType]];
    const [draggedItem] = newImageList.splice(dragIndex, 1);
    newImageList.splice(hoverIndex, 0, draggedItem);

    handleAssetChange({ ...editingAsset, [currentImageType]: newImageList });
  };

  const handleTagChange = (imageIdx: number, newTag: string) => {
    if (!imageCategories[currentImageType]) return;

    const newImageList = [...imageCategories[currentImageType]];
    newImageList[imageIdx] = { ...newImageList[imageIdx], imageTag: newTag };

    handleAssetChange({ ...editingAsset, [currentImageType]: newImageList });
  };

  const handleDelete = (imageIdx: number) => {
    if (!imageCategories[currentImageType]) return;

    const imageToDelete = imageCategories[currentImageType][imageIdx];
    if (imageToDelete?.isNew && imageToDelete.imageUrlCf.startsWith('blob:')) {
      URL.revokeObjectURL(imageToDelete.imageUrlCf);
    }
  
    const newImageList = [...imageCategories[currentImageType]];
    newImageList.splice(imageIdx, 1);
  
    handleAssetChange({ ...editingAsset, [currentImageType]: newImageList });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentImageType(categoryKeys[newValue]);
  };

  if (categoryKeys.length === 0) {
    return <Typography sx={{ p: 2 }}>No image categories found.</Typography>;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      {/* This structure can be simplified now that the parent handles scrolling */}
      <Box>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs 
            value={categoryKeys.indexOf(currentImageType)} 
            onChange={handleTabChange} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{
              '& .Mui-selected': {
                color: 'var(--ums-accent-red) !important', // Makes the text of the selected tab red
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'var(--ums-accent-red)', // Makes the underline red
              },
            }}
          >
            {categoryKeys.map((type) => (
              <Tab key={type} label={`${type.replace('image', '')} (${imageCategories[type]?.length || 0})`} />
            ))}
          </Tabs>
        </Box>
        
        <Box>
          {imageCategories[currentImageType]?.length > 0 ? (
            imageCategories[currentImageType].map((image, idx) => (
              <DraggableImageItem
                key={`${currentImageType}-${idx}-${image.imageUrlCf}`}
                image={image}
                index={idx}
                moveImage={moveImage}
                onTagChange={handleTagChange}
                onDelete={handleDelete}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No images in this category. Upload some images to get started.
            </Typography>
          )}
        </Box>
      </Box>
    </DndProvider>
  );
};

export default ImageOrderList;